import React, { useEffect, useState, useMemo, useRef } from 'react';
import { ListAccountSymbolsReply, ListAccountSymbolsReq, Symbol } from '../../../proto/account-manager_pb';
import { useAccountClient } from '../../../client/client-hook';
import useAuth from '../../../hooks/useAuth';
import Loader from '../../../components/Loader';
import { Autocomplete, SxProps, TextField } from '@mui/material';
import { Theme } from '@mui/material/styles';

const SymbolAutocompleteWrapper = ({
  id = 'auto-complete',
  label = 'Autocomplete Label',
  sx = {},
  error = false,
  defaultValue = '',
  disabled = false,
  selectedAccountId,
  popperSx,
  onOptionSelect
}: {
  id?: string;
  label?: string;
  sx?: SxProps<Theme>;
  error?: boolean;
  defaultValue?: string;
  disabled?: boolean;
  selectedAccountId: string;
  /** Popper overrides, e.g. zIndex when rendered inside a raised dialog. */
  popperSx?: SxProps<Theme>;
  onOptionSelect: (value: Symbol.AsObject | null) => void;
}) => {
  const [allSymbols, setAllSymbols] = useState<Symbol.AsObject[]>([]);
  const [displayedSymbols, setDisplayedSymbols] = useState<Symbol.AsObject[]>([]);
  const [paginationCursor, setPaginationCursor] = useState('');
  const [reachedBottom, setReachedBottom] = useState(false);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [internalValue, setInternalValue] = useState<Symbol.AsObject | null>(null);
  const { user } = useAuth();
  const debounceTimer = useRef<NodeJS.Timeout>();
  const accountClient = useAccountClient();
  const isInitialMount = useRef(true);

  const emptySymbol: Symbol.AsObject = useMemo(
    () => ({
      id: 0,
      name: '',
      enabled: true,
      tradeModeEnabled: true
    }),
    []
  );

  // Initialize internal value from defaultValue
  useEffect(() => {
    if (isInitialMount.current && defaultValue) {
      const foundSymbol = allSymbols.find((x) => x.name === defaultValue);
      setInternalValue(foundSymbol || { ...emptySymbol, name: defaultValue });
    }
    isInitialMount.current = false;
  }, [defaultValue, allSymbols, emptySymbol]);

  const fetchSymbols = async (accountId: string, searchQuery = '', reset = false) => {
    if (!user?.tcId || !accountId || isLoading) return;

    try {
      setIsLoading(true);
      const client = await accountClient();
      const req: ListAccountSymbolsReq.AsObject = {
        userId: user.tcId,
        accountId,
        query: searchQuery,
        pageSize: 50,
        paginationCursor: reset ? '' : paginationCursor
      };

      const res = await client.listAccountSymbols(req);
      const result = res as ListAccountSymbolsReply.AsObject;
      const newSymbols = result.symbolsList.filter((x) => x.tradeModeEnabled);

      if (reset) {
        setAllSymbols(newSymbols);
        setDisplayedSymbols(newSymbols);
      } else {
        const updatedSymbols = [...allSymbols, ...newSymbols.filter((s) => !allSymbols.some((p) => p.name === s.name))];
        setAllSymbols(updatedSymbols);
        setDisplayedSymbols(updatedSymbols);
      }

      setPaginationCursor(result.nextPageCursor);
      setReachedBottom(result?.symbolsList?.length < 50);
    } catch (err) {
      console.error('Error fetching symbols:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedAccountId) {
      fetchSymbols(selectedAccountId, '', true);
    }
  }, [selectedAccountId]);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      if (query.trim() === '') {
        setDisplayedSymbols(allSymbols);
      } else {
        const filtered = allSymbols.filter((s) => s.name.toLowerCase().includes(query.toLowerCase()));
        setDisplayedSymbols(filtered);
        if (filtered.length < 5) {
          fetchSymbols(selectedAccountId, query, true);
        }
      }
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [query, selectedAccountId, allSymbols]);

  const handleScroll = async (event: React.SyntheticEvent) => {
    const target = event.currentTarget;
    const scrollBottom = target.scrollTop + target.clientHeight;

    if (Math.abs(target.scrollHeight - scrollBottom) <= 1 && !isLoading && !reachedBottom) {
      await fetchSymbols(selectedAccountId, query);
    }
  };

  const handleChange = (event: any, newValue: any) => {
    setInternalValue(newValue);
    onOptionSelect(newValue);
    if (newValue === null) {
      setQuery('');
      setDisplayedSymbols(allSymbols);
    }
  };

  const handleInputChange = (event: any, newInputValue: string) => {
    setQuery(newInputValue);
  };

  const handleOpen = () => {
    // Don't reset query when opening - maintain current search
    if (query.trim() === '') {
      fetchSymbols(selectedAccountId, '', true);
    } else {
      // Only fetch if we don't have enough local matches
      const filtered = allSymbols.filter((s) => s.name.toLowerCase().includes(query.toLowerCase()));
      if (filtered.length < 5) {
        fetchSymbols(selectedAccountId, query, true);
      }
    }
  };

  const isOptionEqualToValue = (option: Symbol.AsObject, value: Symbol.AsObject) => {
    return option?.name === value?.name;
  };

  return (
    <>
      {isLoading && <Loader />}
      <Autocomplete
        fullWidth
        disableClearable={false}
        clearOnBlur={false}
        id={id}
        value={internalValue}
        options={displayedSymbols}
        onChange={handleChange}
        onInputChange={handleInputChange}
        filterOptions={(options) => options}
        disabled={disabled}
        ListboxProps={{
          onScroll: handleScroll
        }}
        componentsProps={popperSx ? { popper: { sx: popperSx } } : undefined}
        onOpen={handleOpen}
        sx={sx}
        loading={isLoading}
        loadingText="Searching..."
        getOptionLabel={(option) => option?.name || ''}
        isOptionEqualToValue={isOptionEqualToValue}
        renderInput={(params) => (
          <TextField {...params} label={label} error={error} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />
        )}
      />
    </>
  );
};

export default SymbolAutocompleteWrapper;
